import { Flex, Pagination, Table } from "@mantine/core";

import { Loading } from "@/components/Loading";
import ScrollableTable from "@/components/ScrollableTable";
import { TextWrapper } from "@/components/TextWrapper";

import BillingSortableTh from "../BillingSortableTh";

import {
  LOAD_ERROR_MESSAGE,
  NO_SEARCH_RESULTS_MESSAGE,
  PRACTICE_LEDGER_COLUMN_SPAN,
  PRACTICE_LEDGER_COLUMNS,
  TABLE_HEADER_HEIGHT,
} from "./constants/practice-ledger";
import LedgerEmptyState from "./LedgerEmptyState";
import LedgerTableRow from "./LedgerTableRow";
import type { PracticeLedgerTableProps } from "./types/ledger-ui";

const LOADING_HEIGHT = 96;

export default function LedgerTable({
  rows,
  totalRows,
  currentPage,
  pageSize,
  isLoading,
  error,
  isSearching = false,
  sortState,
  onSort,
  onPageChange,
}: PracticeLedgerTableProps) {
  const totalPages = totalRows > 0 ? Math.ceil(totalRows / pageSize) : 0;

  return (
    <>
      <ScrollableTable withRowBorders data-testid="practice-ledger-table">
        <Table.Thead bg="cream.2">
          <Table.Tr h={TABLE_HEADER_HEIGHT}>
            {PRACTICE_LEDGER_COLUMNS.map((column) =>
              column.sortKey ? (
                <BillingSortableTh
                  labelSize="md"
                  onSort={onSort}
                  w={column.width}
                  key={column.key}
                  ta={column.align}
                  label={column.label}
                  sortState={sortState}
                  columnKey={column.sortKey}
                  testId={`practice-ledger-sort-${column.key}`}
                />
              ) : (
                <Table.Th w={column.width} key={column.key} ta={column.align}>
                  <TextWrapper size="md" c="dark.9" variant="bold">
                    {column.label}
                  </TextWrapper>
                </Table.Th>
              ),
            )}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {isLoading && (
            <Table.Tr data-testid="practice-ledger-table-loading">
              <Table.Td colSpan={PRACTICE_LEDGER_COLUMN_SPAN}>
                <Loading height={LOADING_HEIGHT} />
              </Table.Td>
            </Table.Tr>
          )}
          {!isLoading && error && (
            <Table.Tr>
              <Table.Td colSpan={PRACTICE_LEDGER_COLUMN_SPAN}>
                <TextWrapper size="md" c="red.7" data-testid="practice-ledger-table-error">
                  {LOAD_ERROR_MESSAGE}
                </TextWrapper>
              </Table.Td>
            </Table.Tr>
          )}
          {!isLoading && !error && rows.length === 0 && (
            <LedgerEmptyState message={isSearching ? NO_SEARCH_RESULTS_MESSAGE : undefined} />
          )}
          {!isLoading && !error && rows.map((row) => <LedgerTableRow row={row} key={row.id} />)}
        </Table.Tbody>
      </ScrollableTable>
      {!isLoading && !error && (
        <Flex justify="center">
          <Pagination
            hideWithOnePage
            total={totalPages}
            value={currentPage}
            onChange={onPageChange}
            data-testid="practice-ledger-pagination"
          />
        </Flex>
      )}
    </>
  );
}
